import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import toast from "react-hot-toast";
import ReportHeader from "../../../reports/components/ReportHeader";
import Loader from "../../../../components/Loader";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { organizationStore } from "../../../../store/organization";
import { userStore } from "../../../../store/user";
import { ACCESS_SCANS_LOCATION_STATS, ACCESS_SCANS_HOURLY_STATS } from "../../../reports/access/access-scans-report/fragment";
import { AccessScansReportRow } from "../../../reports/access/access-scans-report/type";
import moment from "moment";
import { fetchShuttleTicketNames, ingressConditions } from "../../accessGateFilters";

const client = createApolloClient();
const { getState } = organizationStore;
const { getState: getUserStore } = userStore;

const SecondAccessCard = () => {
  const organizationId = getState().organizationId;
  const user = getUserStore().user;
  const [isLoading, setIsLoading] = useState(true);
  const [locationData, setLocationData] = useState<any[]>([]);
  const [hourlyData, setHourlyData] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Get today's scans (5am to 5am next day)
        const now = moment();
        let todayDayStart: moment.Moment;

        if (now.hour() < 5) {
          // Before 5am today, get yesterday 5am to today 5am
          todayDayStart = moment().subtract(1, 'day').hour(5).minute(0).second(0);
        } else {
          // After 5am today, get today 5am to tomorrow 5am
          todayDayStart = moment().hour(5).minute(0).second(0);
        }

        const todayDayEnd = moment(todayDayStart).add(1, 'day');
        const todayStart = todayDayStart.format('YYYY-MM-DD HH:mm:ss');
        const todayEnd = todayDayEnd.format('YYYY-MM-DD HH:mm:ss');

        // Get yesterday's scans (5am to 5am)
        const yesterdayDayStart = moment(todayDayStart).subtract(1, 'day');
        const yesterdayDayEnd = moment(yesterdayDayStart).add(1, 'day');
        const yesterdayStart = yesterdayDayStart.format('YYYY-MM-DD HH:mm:ss');
        const yesterdayEnd = yesterdayDayEnd.format('YYYY-MM-DD HH:mm:ss');

        // Build base conditions
        let baseConditions: any[] = [
          { organization_id: { _eq: organizationId } },
        ];

        if (user?.events) {
          baseConditions.push({ event_id: { _in: user?.events } });
        }

        // Shuttle tickets are boarding scans, not gate entries (see accessGateFilters.ts)
        const shuttleTicketNames = await fetchShuttleTicketNames(client, organizationId, user?.events);
        const gateEntry = ingressConditions(shuttleTicketNames);

        // Successful scans through an access gate into the park
        const todayIngressWhere = {
          _and: [
            ...baseConditions,
            { adjusted_scan_time: { _gte: todayStart } },
            { adjusted_scan_time: { _lte: todayEnd } },
            ...gateEntry,
          ],
        };

        const yesterdayIngressWhere = {
          _and: [
            ...baseConditions,
            { adjusted_scan_time: { _gte: yesterdayStart } },
            { adjusted_scan_time: { _lte: yesterdayEnd } },
            ...gateEntry,
          ],
        };

        // Fetch location data (only location_name field for ingress scans)
        const [todayLocationResponse, todayHourlyResponse, yesterdayHourlyResponse] = await Promise.all([
          client.query({
            query: ACCESS_SCANS_LOCATION_STATS,
            variables: {
              where: todayIngressWhere,
            },
            fetchPolicy: 'network-only',
          }),
          client.query({
            query: ACCESS_SCANS_HOURLY_STATS,
            variables: {
              where: todayIngressWhere,
            },
            fetchPolicy: 'network-only',
          }),
          client.query({
            query: ACCESS_SCANS_HOURLY_STATS,
            variables: {
              where: yesterdayIngressWhere,
            },
            fetchPolicy: 'network-only',
          })
        ]);

        const todayScans = todayHourlyResponse?.data?.reports_access || [];
        const yesterdayScans = yesterdayHourlyResponse?.data?.reports_access || [];
        const locationScans = todayLocationResponse?.data?.reports_access || [];

        console.log('SecondAccessCard - Today scans:', todayScans.length);
        console.log('SecondAccessCard - Yesterday scans:', yesterdayScans.length);
        console.log('SecondAccessCard - Location scans:', locationScans.length);

        // Process location data (top 5 busiest entry points - today only)
        // Data is already filtered for ingress scans
        const locationCounts: Record<string, number> = {};
        locationScans.forEach((scan: AccessScansReportRow) => {
          const location = scan.location_name || 'Unknown';
          locationCounts[location] = (locationCounts[location] || 0) + 1;
        });

        const sortedLocations = Object.entries(locationCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 5)
          .map(([location, count]) => ({
            location,
            count,
          }));

        console.log('SecondAccessCard - Location counts:', locationCounts);
        console.log('SecondAccessCard - Top 5 locations:', sortedLocations);
        setLocationData(sortedLocations);

        // Process hourly data (today vs yesterday - ingress only)
        const todayHourly: Record<number, number> = {};
        const yesterdayHourly: Record<number, number> = {};

        // Initialize all hours to 0
        for (let i = 0; i < 24; i++) {
          todayHourly[i] = 0;
          yesterdayHourly[i] = 0;
        }

        // Count today's ingress scans by hour (already filtered for ingress)
        todayScans.forEach((scan: AccessScansReportRow) => {
          if (scan.adjusted_scan_time) {
            const hour = moment.utc(scan.adjusted_scan_time).hour();
            todayHourly[hour]++;
          }
        });

        // Count yesterday's ingress scans by hour (already filtered for ingress)
        yesterdayScans.forEach((scan: AccessScansReportRow) => {
          if (scan.adjusted_scan_time) {
            const hour = moment.utc(scan.adjusted_scan_time).hour();
            yesterdayHourly[hour]++;
          }
        });

        // Format hourly data starting at 5am
        const hourlyChartData = Array.from({ length: 24 }, (_, i) => {
          const hour = (5 + i) % 24;
          const displayHour = (5 + i) % 24;

          const formatHour = (h: number) => {
            const period = h < 12 || h === 24 ? 'am' : 'pm';
            const formattedHour = (h % 12) || 12;
            return `${formattedHour}${period}`;
          };

          return {
            hour: formatHour(displayHour),
            today: todayHourly[hour] || 0,
            yesterday: yesterdayHourly[hour] || 0,
          };
        });

        console.log('Hourly data:', hourlyChartData);
        setHourlyData(hourlyChartData);
      } catch (error) {
        console.error("Error fetching access data:", error);
        toast.error("Error fetching access data.");
      } finally {
        setIsLoading(false);
      }
    };

    if (organizationId || user) fetchData();
  }, [organizationId, user]);

  const countFormatter = (value: number) => {
    return value.toLocaleString();
  };

  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3 p-5 bg-white shadow">
      {/* Widget 3: Busiest Entry Points */}
      <div className="w-full rounded-lg grid grid-cols-1 md:grid-cols-1 bg-gray-200 shadow">
        <div className="justify-items-center">
          <ReportHeader
            title={`Busiest Entry Points - Today`}
            toolTipShow={true}
            tooltipText={
              "Top 5 access gates with the most entries into the park today (5am to 5am). Successful gate entries only: no egress scans and no shuttle ticket scans."
            }
          />
        </div>
        <div className="bg-white rounded-md p-1 mx-3 m-2" style={{ height: '400px' }}>
          {isLoading ? (
            <div className="w-full h-full grid justify-items-center content-center">
              <Loader />
            </div>
          ) : locationData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={locationData}
                margin={{ top: 30, right: 10, left: 0, bottom: 80 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="location"
                  tick={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    fill: "#4a5568",
                  }}
                  angle={-30}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  width={80}
                  tickFormatter={countFormatter}
                  tick={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    fill: "#4a5568",
                  }}
                />
                <Tooltip
                  contentStyle={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                    color: "#4a5568",
                  }}
                  formatter={countFormatter}
                />
                <Bar dataKey="count" fill="#B71C1C" name="Ingress Scans" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full grid justify-items-center content-center">
              <span className="text-gray-500">No scan data available</span>
            </div>
          )}
        </div>
      </div>

      {/* Widget 4: Hourly Entry Traffic */}
      <div className="w-full rounded-lg grid grid-cols-1 md:grid-cols-1 bg-gray-200 shadow">
        <div className="justify-items-center">
          <ReportHeader
            title="Hourly Entry Traffic - Today vs Yesterday"
            toolTipShow={true}
            tooltipText={
              "Entries through the access gates into the park by hour, today vs yesterday (5am to 5am). Successful gate entries only: no egress scans and no shuttle ticket scans. Hours start at 5am."
            }
          />
        </div>
        <div className="bg-white rounded-md p-1 mx-3 m-2" style={{ height: '400px' }}>
          {isLoading ? (
            <div className="w-full h-full grid justify-items-center content-center">
              <Loader />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={hourlyData}
                margin={{ top: 30, right: 10, left: 0, bottom: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="hour"
                  tick={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    fill: "#4a5568",
                  }}
                  interval={2}
                />
                <YAxis
                  width={100}
                  tickFormatter={countFormatter}
                  tick={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    fill: "#4a5568",
                  }}
                />
                <Tooltip
                  formatter={countFormatter}
                  contentStyle={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                    color: "#4a5568",
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="today"
                  name="Today"
                  stroke="#B71C1C"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="yesterday"
                  name="Yesterday"
                  stroke="#4a5568"
                  strokeWidth={2}
                  dot={false}
                  strokeDasharray="5 5"
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default SecondAccessCard;
